import clonedeep from 'lodash.clonedeep';
import moment from 'moment';

import {
  getStopoverDataWithDataKey,
  getStopoverGroupWithSelectedStopover,
  getStopoverOptionWithSelectedStopover,
} from '../BookingStopoverOptions/helpers';
import { accomTypes, getOption } from '../BookingFieldset/helpers';

const packageKeys = {
  accommodations: 'accommodationOptions',
  excursions: 'excursionOptions',
  extras: 'extras',
  flights: 'flightOptions',
  stopovers: 'stopoverOptions',
  tickets: 'ticketOptions',
  transports: 'transportOptions',
};

const selectedOptionKeys = {
  accommodations: 'accommodations',
  excursions: 'excursions',
  extras: 'extras',
  flights: 'flights',
  stopovers: 'stopovers',
  tickets: 'tickets',
  transports: 'transports',
};

const progressSteps = {
  ACKNOWLEDGEMENT: 'Acknowledgement',
  EVENT_OPTION: 'EventOption',
  PASSENGER_DETAILS: 'PassengerDetails',
  PAYMENT: 'Payment',
  SUMMARY: 'Summary',
  STOPOVER_OPTION: 'StopoverOption',
  TRAVEL_OPTION: 'TravelOption',
};

const itineraryItemKeys = {
  excursions: 'excursion',
  hotel: 'hotel',
  flights: 'flight',
  motorhome: 'motorhome',
  tickets: 'ticket',
  transports: 'coach',
};

export const PAX_ERRORS = {
  PAX_SELECTION_TOO_BIG: 169,
  PAX_SELECTION_TOO_SMALL: 170,
  PAX_TYPE_NOT_ALLOWED: 171,
  PAX_TYPE_EXCEEDS_LIMIT: 172,
  PAX_TYPE_MISSING_REQUIRED: 179,
  PAX_GENERIC_INVALID_DATE_OF_BIRTH: 177,
};

// Specify the options that can be dynamically updated on the specific page
const eventOptionDynamicSelectedOptionKeys = [
  selectedOptionKeys.accommodations,
  selectedOptionKeys.excursions,
  selectedOptionKeys.extras,
  selectedOptionKeys.flights,
];

const stopoverOptionDynamicItinerarySelectedOptionKeys = [];

const travelOptionDynamicItinerarySelectedOptionKeys = [
  selectedOptionKeys.flights,
  selectedOptionKeys.transports,
];

// Check if flights in state are empty and set showAtolLabel
// accordingly (to show if flights are selected).
export function isAtolProtected(selectedOptionsFlights) {
  const isFlightsEmpty =
    Object.values(selectedOptionsFlights).filter((value) => {
      if (value != null) return Object.entries(value).length !== 0;
      return false;
    }).length <= 0;
  const showAtolLabel = selectedOptionsFlights.length > 0 && !isFlightsEmpty;

  return showAtolLabel;
}

export function removeEmptySlots(array = []) {
  return array.reduce((acc, item) => {
    if (item) {
      acc.push(item);
    }
    return acc;
  }, []);
}

export function getSelectedOptionWithGroupKey(selectedGroupOptions, groupKey) {
  return selectedGroupOptions.find((selectedGroup) => selectedGroup.groupKey === groupKey);
}

export function getPackageOptionsGroupWithGroupKey(packageOptions, groupKey) {
  return packageOptions.find((group) => group.key === groupKey);
}

export function getGroupOptionWithOptionKey(allGroupOptions, optionKey) {
  return allGroupOptions.find((option) => option.key === optionKey);
}

export function getOptionOptionWithOptionKey(allGroupOptions, optionKey) {
  let found;
  allGroupOptions.forEach((groupOption) => {
    groupOption.options?.forEach((option) => {
      if (option.key === optionKey) found = option;
    });
  });

  return found;
}

export function formatTitle(prefix, suffix) {
  if (!suffix || suffix === '') return prefix;
  return `${prefix}: ${suffix}`;
}

export function mergeDefaultAndUpgrades(option) {
  let mergedOptions = [];
  if (option?.defaultOption) {
    mergedOptions.push(option.defaultOption);
  }

  if (option?.upgrades) {
    mergedOptions = mergedOptions.concat(option.upgrades);
  }
  return mergedOptions;
}

/**
 * Mutate the itinerary for that day
 * @param {Array} itinerary
 * @param {Number} day
 * @param {String} mappingKey
 * @param {String} itineraryDescription
 */
function mutateItineraryItemContent(itinerary, day, mappingKey, itineraryDescription) {
  const itineraryDay = itinerary.find((itineraryItem) => itineraryItem.day === day);
  if (!itineraryDay) return;
  const dayItem = itineraryDay.items.find((item) => item.type === itineraryItemKeys[mappingKey]);
  if (!dayItem) return;
  // Tickets and Flights utilize CMS set initial itineraryDescription as a prefix
  if (
    dayItem.content &&
    (mappingKey === selectedOptionKeys.tickets || mappingKey === selectedOptionKeys.flights)
  ) {
    dayItem.content = `${dayItem.content} ${itineraryDescription}`;
  } else {
    dayItem.content = itineraryDescription;
  }
}

/**
 * Mutate the itinerary for that day or days
 * @param {Array} itinerary
 * @param {Number | Number[]} day
 * @param {String} mappingKey
 * @param {String} itineraryDescription
 */
function mutateItinerary(itinerary, day, mappingKey, itineraryDescription) {
  const days = typeof day === 'number' ? [day] : day;

  days.forEach((d) => {
    mutateItineraryItemContent(itinerary, d, mappingKey, itineraryDescription);
  });
}

/**
 * Function iterates across all selected options for a single tour component such as Tickets and finds
 * and updates all the descriptions for that tour component
 * @param {Array} itinerary mutates this incoming itinerary
 * @param {Object} selectedOptions state from options page
 * @param {Object} packageOptions the entire package options object from API call
 * @param {String} mappingKey a key that can used to identify the correct key for each tour component across different schemas
 */
function injectItineraryDescription(itinerary, selectedOptions, packageOptions, mappingKey) {
  selectedOptions?.forEach((selectedOption) => {
    // selectedOption can be undefined if removed from state
    if (!selectedOption) return;
    // Find option in package for selected option
    const packageOptionGroups = packageOptions[packageKeys[mappingKey]];
    // Most Option Schemas contain defaultOption, and the upgrades array, but excursions just have excursions,
    // here we capture all
    const {
      defaultOption = {},
      upgrades = [],
      options = [],
      day = 0,
    } = getPackageOptionsGroupWithGroupKey(packageOptionGroups, selectedOption.groupKey) || {};
    const allOptions = [defaultOption, ...upgrades, ...options];
    let selectedPackageOption;
    // Transports have a slightly different model schema
    if (mappingKey === selectedOptionKeys.transports) {
      selectedPackageOption = getOptionOptionWithOptionKey(allOptions, selectedOption.optionKey);
    } else {
      selectedPackageOption = getGroupOptionWithOptionKey(allOptions, selectedOption.optionKey);
    }

    let itineraryDescription =
      selectedPackageOption?.itineraryDescription ||
      selectedPackageOption?.option?.itineraryDescription;

    // if selectedPackageOption is not found, there will be no description
    if (!itineraryDescription) return;

    // Flights postfix the flight class title
    if (mappingKey === selectedOptionKeys.flights) {
      const { option } =
        selectedPackageOption.flightClasses.find(
          ({ key }) => key === selectedOption.flightClassKey
        ) || {};
      itineraryDescription += ` ${option?.itineraryDescription}`;
    }

    // Finally Mutate Itinerary
    mutateItinerary(
      itinerary,
      day,
      mappingKey === selectedOptionKeys.accommodations
        ? selectedPackageOption.accommodationType
        : mappingKey,
      itineraryDescription
    );
  });
}

/**
 * The itinerary data dependent on the particular option page will be required dynamically update
 * the itinerary descriptions. I've extracted this data crunching task out of the option pages
 * and into these helpers, to keep code dry and avoid bloat of option page files. One thing to note
 * is that across the tour component types there are some slight differences that are handled
 * with the odd conditional dependant on it type.
 *
 * This function always begins with the original itinerary from the API and iterates over every
 * selected tour component and updates it to the correct description.
 *
 * @param {Object} selectedOptions state from options page
 * @param {Object} packageOptions the entire package options object from API call
 * @param {String} selectedStep current selected step identifying the page
 * @returns Array itinerary
 */
export function injectItineraryDescriptions(allSelectedOptions, packageOptions, selectedStep) {
  const defaultItinerary = packageOptions?.itinerarySideBar?.itinerary;
  // defaultItinerary comes from props, copy to mutate and return as state
  const itinerary = clonedeep(defaultItinerary);
  // Iterate over all selectedOptions
  Object.keys(allSelectedOptions).forEach((selectedOptionsKey) => {
    // Only valid options can be updated dependent on booking option step
    const [mappingKey] =
      Object.entries(selectedOptionKeys).find(([, value]) => value === selectedOptionsKey) || [];

    const selectedOptionCanBeUpdated =
      (selectedStep === progressSteps.EVENT_OPTION &&
        eventOptionDynamicSelectedOptionKeys.includes(selectedOptionsKey)) ||
      (selectedStep === progressSteps.STOPOVER_OPTION &&
        stopoverOptionDynamicItinerarySelectedOptionKeys.includes(selectedOptionsKey)) ||
      (selectedStep === progressSteps.TRAVEL_OPTION &&
        travelOptionDynamicItinerarySelectedOptionKeys.includes(selectedOptionsKey));

    if (selectedOptionCanBeUpdated) {
      injectItineraryDescription(
        itinerary,
        allSelectedOptions[selectedOptionsKey],
        packageOptions,
        mappingKey
      );
    }
  });

  return itinerary;
}

export function buildCapacityErrorMessage(capacity, partySize, fillRooms, labels) {
  let errorMessage = '';
  if (capacity === 0) {
    errorMessage = labels.validationNoRoomSelected;
  } else if (capacity / 2 > partySize) {
    errorMessage = labels.validationTooManyRoomsSelected
      .replace('[capacity]', capacity)
      .replace('[partySize]', partySize);
  } else if (capacity < partySize) {
    errorMessage = labels.validationSelectMoreRooms
      .replace('[capacity]', capacity)
      .replace('[partySize]', partySize);
  } else if (fillRooms && capacity !== partySize) {
    errorMessage = labels.validationCompleteAllRooms
      .replace('[capacity]', capacity)
      .replace('[partySize]', partySize);
  }
  return errorMessage;
}

export function buildStopoverItineraryDescriptions(
  stopoverOptions,
  selectedStopovers,
  type,
  startDate
) {
  const stopover = selectedStopovers.find((selectedStopover) => {
    const { stopoverType } =
      getStopoverGroupWithSelectedStopover(stopoverOptions, selectedStopover) || {};
    return stopoverType === type;
  });

  let stopoverItinerary = null;
  if (stopover) {
    const {
      option: {
        itinerary: defaultItinerary = [],
        itineraryDescription = '',
        hotelOption = {},
        excursionOption = {},
      },
    } = getStopoverOptionWithSelectedStopover(stopoverOptions, stopover) || {};
    const { numberOfNights, flightOptions } = getStopoverDataWithDataKey(
      stopoverOptions,
      stopover.dataKey
    );

    const itinerary = clonedeep(defaultItinerary)
      .map((itineraryItem) => ({
        ...itineraryItem,
        ...(startDate && {
          date: moment(startDate)
            .add(itineraryItem.day - 1, 'd')
            .toISOString(),
        }),
      }))
      .slice(0, numberOfNights);

    const allHotelOptions = [hotelOption.defaultOption, ...hotelOption.upgrades];
    const selectedHotelOption =
      allHotelOptions.find(({ key }) => key === stopover.hotel.optionKey) || {};

    mutateItineraryItemContent(
      itinerary,
      hotelOption.day,
      accomTypes.HOTEL,
      selectedHotelOption.option.itineraryDescription
    );

    if (stopover.excursion) {
      const selectedExcursionOption =
        excursionOption.options.find(({ key }) => key === stopover.excursion.optionKey) || {};

      mutateItineraryItemContent(
        itinerary,
        excursionOption.day,
        selectedOptionKeys.excursions,
        selectedExcursionOption.itineraryDescription
      );
    }

    if (stopover.flights) {
      stopover.flights.forEach((stopoverFlight) => {
        const flightGroup = flightOptions.find(
          (flightOption) => flightOption.key === stopoverFlight.groupKey
        );
        const flightOption = getOption(flightGroup, stopoverFlight.optionKey);

        mutateItineraryItemContent(
          itinerary,
          flightGroup.day,
          selectedOptionKeys.flights,
          flightOption.option.itineraryDescription
        );
      });
    }

    stopoverItinerary = {
      itineraryDescription,
      itinerary,
    };
  }

  return stopoverItinerary;
}

/**
 * Formats the error message for pax validation errors based on the error code
 * @param {Object} params - Parameters for error message formatting
 * @param {number} params.errorCode - The error code from the API response
 * @param {Object} params.labels - CMS labels for error messages
 * @param {Array} params.paxTypes - Array of passenger type definitions
 * @param {Object} [params.paxTypeWithMissing] - Optional, pre-calculated missing pax type data
 * @returns {string} Formatted error message
 */
export function formatPaxValidationErrorMessage({
  errorCode,
  labels,
  paxTypes,
  paxTypeWithMissing,
}) {
  const getErrorMessage = (label, defaultMessage) => label || defaultMessage;

  switch (errorCode) {
    case PAX_ERRORS.PAX_SELECTION_TOO_BIG:
      return getErrorMessage(
        labels.validationPaxCombinationPartyTooBig,
        'The selected combination of passengers exceeds the maximum party size.'
      );

    case PAX_ERRORS.PAX_SELECTION_TOO_SMALL:
      return getErrorMessage(
        labels.validationPaxCombinationPartyTooSmall,
        'The selected combination of passengers is below the minimum party size.'
      );

    case PAX_ERRORS.PAX_TYPE_NOT_ALLOWED:
      return getErrorMessage(
        labels.validationPaxCombinationNotSupported,
        'The selected passenger types are not allowed for this combination.'
      );

    case PAX_ERRORS.PAX_TYPE_EXCEEDS_LIMIT:
      return getErrorMessage(
        labels.validationPaxCombinationExceedsLimitations,
        'The selected passenger counts exceed the allowed limits.'
      );

    case PAX_ERRORS.PAX_TYPE_MISSING_REQUIRED:
      if (paxTypeWithMissing) {
        const paxTypeName = paxTypeWithMissing.name || paxTypeWithMissing.key;
        const requiredPaxTypeNames = paxTypeWithMissing.requiredPaxTypeKeys
          .map((reqKey) => {
            const paxType = paxTypes?.find((pt) => pt.key === reqKey);
            return paxType?.name || reqKey;
          })
          .join(', ');

        return getErrorMessage(
          labels.validationPaxTypeMissingRequiredPaxType,
          'A required passenger type is missing for [PaxType]. Required types: [RequiredPaxTypes].'
        )
          .replace(/\[PaxType\]/g, paxTypeName)
          .replace(/\[RequiredPaxTypes\]/g, requiredPaxTypeNames);
      }
      return getErrorMessage(
        labels.validationPaxTypeMissingRequiredPaxType,
        'A required passenger type is missing.'
      );

    default:
      return 'An error occurred with the passenger selection.';
  }
}

/**
 * Validates passenger selection against defined rules.
 * @param {Object} paxSelection - Selected passenger counts by pax key.
 * @param {Array} paxRules - Array of rules defining valid passenger combinations.
 * @param {Object} labels - CMS labels for error messages.
 * @param {Array} paxTypes - Array of passenger type definitions.
 * @returns {Array} - Array of validation errors.
 */
export function validatePaxSelection(
  paxSelection = {},
  paxRules = [],
  labels = {},
  paxTypes = [],
  setIsCallToBook = undefined
) {
  if (!paxSelection || !paxRules || !paxRules.length) {
    return [];
  }

  const errors = [];
  const totalSelected = Object.values(paxSelection).reduce((sum, count) => sum + count, 0);
  const selectedPaxTypes = Object.keys(paxSelection).filter((key) => paxSelection[key] > 0);

  // Helper function to create error message and handle call to book
  const createError = (errorCode, additionalData = {}) => {
    if (setIsCallToBook) {
      setIsCallToBook(true);
    }
    return {
      message: formatPaxValidationErrorMessage({
        errorCode,
        labels,
        paxTypes,
        ...additionalData,
      }),
    };
  };

  // TourPaxSelectionTooBig
  if (paxRules.every((rule) => totalSelected > rule.maximumPartySize)) {
    return [createError(PAX_ERRORS.PAX_SELECTION_TOO_BIG)];
  }

  // TourPaxSelectionTooSmall
  if (paxRules.every((rule) => totalSelected < rule.minimumPartySize)) {
    return [createError(PAX_ERRORS.PAX_SELECTION_TOO_SMALL)];
  }

  // Find applicable rules (matching backend logic)
  const applicableRules = paxRules.filter((rule) => {
    // All selected pax types must be allowed in this rule
    const allSelectedPaxTypesAllowed = selectedPaxTypes.every((key) =>
      rule.paxRules.some((pr) => pr.paxTypeKey === key)
    );

    // All rule pax types must either be selected or have minimumPaxCount of 0
    const allRequiredPaxTypesSelected = rule.paxRules.every(
      // eslint-disable-next-line no-prototype-builtins
      (pr) => paxSelection.hasOwnProperty(pr.paxTypeKey) || pr.minimumPaxCount === 0
    );

    return (
      allSelectedPaxTypesAllowed &&
      allRequiredPaxTypesSelected &&
      totalSelected <= rule.maximumPartySize &&
      totalSelected >= rule.minimumPartySize
    );
  });

  // TourPaxSelectionPaxTypeNotAllowed
  if (!applicableRules.length) {
    return [createError(PAX_ERRORS.PAX_TYPE_NOT_ALLOWED)];
  }

  // TourPaxTypeSelectionExceedsLimit
  const validRule = applicableRules.find((rule) =>
    rule.paxRules.every((paxRule) => {
      const count = paxSelection[paxRule.paxTypeKey] || 0;
      return count >= paxRule.minimumPaxCount && count <= paxRule.maximumPaxCount;
    })
  );

  if (!validRule) {
    return [createError(PAX_ERRORS.PAX_TYPE_EXCEEDS_LIMIT)];
  }

  // TourPaxSelectionMissingRequiredOtherPaxType
  const paxTypeWithMissing = paxTypes
    .filter((pt) => selectedPaxTypes.includes(pt.key) && pt.requiredPaxTypeKeys?.length > 0)
    .find((pt) => pt.requiredPaxTypeKeys.every((reqKey) => !selectedPaxTypes.includes(reqKey)));

  if (paxTypeWithMissing) {
    return [createError(PAX_ERRORS.PAX_TYPE_MISSING_REQUIRED)];
  }
  return errors;
}

export default null;
